import { useState, useEffect } from 'react';
import { useAuth } from "../../contexts/AuthContext";
import { db } from "../../firebase";
import { doc, getDoc, updateDoc, arrayUnion, arrayRemove, writeBatch, runTransaction, setDoc } from "firebase/firestore";
import { useNavigate, useParams } from "react-router";
import * as theme from '../../theme';
import Heading from '../../components/Heading';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import Box from '@mui/material/Box';
import { onSnapshot } from "firebase/firestore";
import { v4 as uuidv4 } from 'uuid';
import { Pagination } from '@mui/material';
import getServerTimestamp from '../../functions/endpoints/GetServerTimestamp';
import dayjs from 'dayjs';
import CircularProgress from '@mui/material/CircularProgress';
import { sendEmail } from '../../functions/endpoints/SendEmail';

export default function Inbox() {
  const { currentUser } = useAuth();
  const { practitionerId } = useParams();
  const navigate = useNavigate();
  const [inboxData, setInboxData] = useState({});
  const [practitioner, setPractitioner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedThread, setExpandedThread] = useState(null);
  const [newThreadOpen, setNewThreadOpen] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newMessageText, setNewMessageText] = useState('');
  const [addMessageTexts, setAddMessageTexts] = useState({}); // {threadId: text}
  const [editingMessages, setEditingMessages] = useState({}); // {msgId: text}
  const currentYear = new Date().getFullYear();
  const lastYear = currentYear - 1;
  const years = [lastYear.toString(), currentYear.toString()];
  const viewerRole = currentUser.role;
  const isPractitioner = viewerRole === 'practitioner';
  const myId = isPractitioner ? practitionerId : 'team'; // 'team' as placeholder for team messages
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const [creatingThread, setCreatingThread] = useState(false);
  const [sendingMessages, setSendingMessages] = useState({}); // {threadId: boolean}
  const [markingThreads, setMarkingThreads] = useState({}); // {threadId: boolean}
  const trubalanceEmail = process.env.REACT_APP_ENVIRONMENT === 'PROD' ? 'hello@trubalancebioenergetics.com' : 'renuhealthtech@gmail.com';

  useEffect(() => {
    const fetchPractitioner = async () => {
      try {
        const snap = await getDoc(doc(db, "practitioners", practitionerId));
        if (snap.exists()) {
          setPractitioner(snap.data());
        } else {
          setError("Practitioner not found");
        }
      } catch (err) {
        setError("Failed to fetch practitioner details: " + err.message);
      }
    };

    fetchPractitioner();

    const unsubs = years.map(year => {
      const docRef = doc(db, `practitioners/${practitionerId}/inbox/${year}`);
      return onSnapshot(docRef, (snap) => {
        setInboxData(prev => ({ ...prev, [year]: snap.data() || { threads: [] } }));
      }, (err) => {
        setError("Failed to load inbox: " + err.message);
      });
    });

    setLoading(false);
    return () => unsubs.forEach(unsub => unsub());
  }, [practitionerId]);

  const getAllThreads = () => {
    let allThreads = [];
    years.forEach(year => {
      if (inboxData[year]) {
        allThreads = [...allThreads, ...(inboxData[year].threads?.map(t => ({ ...t, year })) || [])];
      }
    });
    return allThreads.sort((a, b) => b.lastModified.toDate() - a.lastModified.toDate());
  };

  const hasUnread = (thread) => {
    const ownUnread = isPractitioner ? thread.practitionerUnread : thread.teamUnread;
    return ownUnread;
  };

  const isOwnMessage = (msg) => {
    return (isPractitioner && msg.authorId === practitionerId) || (!isPractitioner && msg.authorId !== practitionerId);
  };

  const hasOtherPartyMessage = (thread) => {
    return thread.messages.some(msg => !isOwnMessage(msg));
  };

  const handleNewThread = async () => {
    if (!newSubject || !newMessageText) return;
    setCreatingThread(true);
    const threadId = uuidv4();
    const msgId = uuidv4();
    const practitionerFirstName = practitioner?.firstName || 'Unknown';
    const displayName = isPractitioner ? practitionerFirstName : "TruBalance Team";
    const timestamp = await getServerTimestamp();
    const isPracSender = isPractitioner;
    const newThread = {
      id: threadId,
      subject: newSubject,
      lastModified: timestamp,
      practitionerUnread: isPracSender ? false : true,
      teamUnread: isPracSender ? true : false,
      messages: [{
        id: msgId,
        authorId: myId,
        date: timestamp,
        text: newMessageText,
        lastModified: timestamp,
        displayName,
      }]
    };
    const docRef = doc(db, `practitioners/${practitionerId}/inbox/${currentYear.toString()}`);
    try {
      const snap = await getDoc(docRef);
      if (snap.exists()) {
        await updateDoc(docRef, { threads: arrayUnion(newThread) });
      } else {
        await setDoc(docRef, { threads: [newThread] });
      }
      if (isPractitioner) {
        // Update global app/inbox for team unreads
        const globalInboxRef = doc(db, "app", "inbox");
        const unreadEntry = { threadId: newThread.id, authorId: practitionerId };
        await setDoc(globalInboxRef, { unreads: arrayUnion(unreadEntry) }, { merge: true });
      }

      // Send email notification
      const recipientEmail = isPractitioner ? trubalanceEmail : process.env.REACT_APP_ENVIRONMENT === 'PROD' ?  practitioner.email : 'jhatlestad@gmail.com';
      const recipientName = isPractitioner ? "TruBalance Team" : `${practitioner.firstName} ${practitioner.lastName}`;
      const prefix = isPractitioner ? "" : "the ";
      const approximateMessageDate = dayjs().format('MM/DD/YYYY h:mm A');
      const emailData = {
        recipientEmail,
        recipientName,
        subject: "New Unread Message",
        templateId: "unread-message",
        dynamicTemplateData: {
          authorName: isPractitioner ? `${practitioner.firstName} ${practitioner.lastName}` : "TruBalance Team",
          prefix,
          messageDate: approximateMessageDate,
        },
        emailType: "unread_message",
        contextData: { threadId, messageId: msgId, practitionerId },
        userId: currentUser.uid,
        isTest: false
      };
      await sendEmail(emailData);

      setNewThreadOpen(false);
      setNewSubject('');
      setNewMessageText('');
    } catch (err) {
      setError("Failed to create thread: " + err.message);
    } finally {
      setCreatingThread(false);
    }
  };

  const handleAddMessage = async (thread) => {
    const text = addMessageTexts[thread.id];
    if (!text) return;
    setSendingMessages(prev => ({ ...prev, [thread.id]: true }));
    const msgId = uuidv4();
    const practitionerFirstName = practitioner?.firstName || 'Unknown';
    const displayName = isPractitioner ? practitionerFirstName : "TruBalance Team";
    const docRef = doc(db, `practitioners/${practitionerId}/inbox/${thread.year}`);
    try {
      await runTransaction(db, async (transaction) => {
        const timestamp = await getServerTimestamp();
        const newMsg = {
          id: msgId,
          authorId: myId,
          date: timestamp,
          text,
          lastModified: timestamp,
          displayName,
        };
        const snap = await transaction.get(docRef);
        const threads = snap.data()?.threads || [];
        const index = threads.findIndex(t => t.id === thread.id);
        if (index === -1) throw new Error("Thread not found");
        const updatedThreads = [...threads];
        const updatedThread = {
          ...updatedThreads[index],
          messages: [...updatedThreads[index].messages, newMsg],
          lastModified: timestamp,
        };
        const isPracSender = isPractitioner;
        const oppositeUnread = isPracSender ? 'teamUnread' : 'practitionerUnread';
        const ownUnread = isPracSender ? 'practitionerUnread' : 'teamUnread';
        updatedThread[oppositeUnread] = true;
        updatedThread[ownUnread] = false; // Mark as read for the sender
        updatedThreads[index] = updatedThread;
        transaction.update(docRef, { threads: updatedThreads });
      });
      if (isPractitioner) {
        // Update global app/inbox for team unreads
        const globalInboxRef = doc(db, "app", "inbox");
        const unreadEntry = { threadId: thread.id, authorId: practitionerId };
        await setDoc(globalInboxRef, { unreads: arrayUnion(unreadEntry) }, { merge: true });
      } else {
        // If team is sending, remove from global unreads since marking as read
        const globalInboxRef = doc(db, "app", "inbox");
        const unreadEntry = { threadId: thread.id, authorId: practitionerId };
        await setDoc(globalInboxRef, { unreads: arrayRemove(unreadEntry) }, { merge: true });
      }

      // Send email notification
      const recipientEmail = isPractitioner ? trubalanceEmail : process.env.REACT_APP_ENVIRONMENT === 'PROD' ?  practitioner.email : 'jhatlestad@gmail.com';
      const recipientName = isPractitioner ? "TruBalance Team" : `${practitioner.firstName} ${practitioner.lastName}`;
      const prefix = isPractitioner ? "" : "the ";
      const approximateMessageDate = dayjs().format('MM/DD/YYYY h:mm A');
      const emailData = {
        recipientEmail,
        recipientName,
        subject: "New Unread Message",
        templateId: "unread-message",
        dynamicTemplateData: {
          authorName: isPractitioner ? `${practitioner.firstName} ${practitioner.lastName}` : "TruBalance Team",
          prefix,
          messageDate: approximateMessageDate,
        },
        emailType: "unread_message",
        contextData: { threadId: thread.id, messageId: msgId, practitionerId },
        userId: currentUser.uid,
        isTest: false
      };
      await sendEmail(emailData);

      setAddMessageTexts(prev => ({ ...prev, [thread.id]: '' }));
    } catch (err) {
      setError("Failed to add message: " + err.message);
    } finally {
      setSendingMessages(prev => ({ ...prev, [thread.id]: false }));
    }
  };

  const handleStartEdit = (msgId, text) => {
    setEditingMessages(prev => ({ ...prev, [msgId]: text }));
  };

  const handleCancelEdit = (msgId) => {
    setEditingMessages(prev => {
      const newPrev = { ...prev };
      delete newPrev[msgId];
      return newPrev;
    });
  };

  const handleSaveEdit = async (thread, msg) => {
    const newText = editingMessages[msg.id];
    if (!newText) return;
    const docRef = doc(db, `practitioners/${practitionerId}/inbox/${thread.year}`);
    try {
      await runTransaction(db, async (transaction) => {
        const timestamp = await getServerTimestamp();
        const snap = await transaction.get(docRef);
        const threads = snap.data()?.threads || [];
        const tIndex = threads.findIndex(t => t.id === thread.id);
        if (tIndex === -1) throw new Error("Thread not found");
        const mIndex = threads[tIndex].messages.findIndex(m => m.id === msg.id);
        if (mIndex === -1) throw new Error("Message not found");
        const updatedThreads = [...threads];
        updatedThreads[tIndex].messages[mIndex] = {
          ...updatedThreads[tIndex].messages[mIndex],
          text: newText,
          lastModified: timestamp,
        };
        updatedThreads[tIndex].lastModified = timestamp;
        transaction.update(docRef, { threads: updatedThreads });
      });
      handleCancelEdit(msg.id);
    } catch (err) {
      setError("Failed to edit message: " + err.message);
    }
  };

  const handleMarkThreadUnread = async (event, thread) => {
    event.stopPropagation();
    event.preventDefault();
    setMarkingThreads(prev => ({ ...prev, [thread.id]: true }));
    const docRef = doc(db, `practitioners/${practitionerId}/inbox/${thread.year}`);
    try {
      await runTransaction(db, async (transaction) => {
        const timestamp = await getServerTimestamp();
        const snap = await transaction.get(docRef);
        const threads = snap.data()?.threads || [];
        const tIndex = threads.findIndex(t => t.id === thread.id);
        if (tIndex === -1) throw new Error("Thread not found");
        const updatedThreads = [...threads];
        const ownUnread = isPractitioner ? 'practitionerUnread' : 'teamUnread';
        updatedThreads[tIndex][ownUnread] = true;
        updatedThreads[tIndex].lastModified = timestamp;
        transaction.update(docRef, { threads: updatedThreads });
      });
      if (!isPractitioner) {
        // Update global app/inbox for team unreads
        const globalInboxRef = doc(db, "app", "inbox");
        const unreadEntry = { threadId: thread.id, authorId: practitionerId };
        await setDoc(globalInboxRef, { unreads: arrayUnion(unreadEntry) }, { merge: true });
      }
    } catch (err) {
      setError("Failed to mark thread as unread: " + err.message);
    } finally {
      setMarkingThreads(prev => ({ ...prev, [thread.id]: false }));
    }
  };

  const handleExpand = (threadId) => (event, isExpanded) => {
    setExpandedThread(isExpanded ? threadId : null);
  };

  const markThreadAsRead = async (threadId) => {
    const thread = getAllThreads().find(t => t.id === threadId);
    if (!thread) return;
    setMarkingThreads(prev => ({ ...prev, [thread.id]: true }));
    const docRef = doc(db, `practitioners/${practitionerId}/inbox/${thread.year}`);
    try {
      await runTransaction(db, async (transaction) => {
        const timestamp = await getServerTimestamp();
        const snap = await transaction.get(docRef);
        const threads = snap.data()?.threads || [];
        const tIndex = threads.findIndex(t => t.id === thread.id);
        if (tIndex === -1) return;
        const updatedThreads = [...threads];
        const ownUnread = isPractitioner ? 'practitionerUnread' : 'teamUnread';
        updatedThreads[tIndex][ownUnread] = false;
        updatedThreads[tIndex].lastModified = timestamp;
        transaction.update(docRef, { threads: updatedThreads });
      });
      if (!isPractitioner) {
        // Update global app/inbox to remove from unreads
        const globalInboxRef = doc(db, "app", "inbox");
        const unreadEntry = { threadId: thread.id, authorId: practitionerId };
        await setDoc(globalInboxRef, { unreads: arrayRemove(unreadEntry) }, { merge: true });
      }
    } catch (err) {
      setError("Failed to mark thread as read: " + err.message);
    } finally {
      setMarkingThreads(prev => ({ ...prev, [thread.id]: false }));
    }
  };

  const allThreads = getAllThreads();
  const totalPages = Math.ceil(allThreads.length / pageSize);
  const indexOfLast = currentPage * pageSize;
  const indexOfFirst = indexOfLast - pageSize;
  const visibleThreads = allThreads.slice(indexOfFirst, indexOfLast);
  const maxSubjectLength = 100;
  const maxMessageLength = 2000;

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;
  if (!practitioner && isPractitioner) return <div>Practitioner data not loaded</div>;

  return (
    <div style={theme.Container.basicWide}>
      <Heading title="Inbox" onClick={() => currentUser.role === 'practitioner' ? navigate(`/dashboard`) : navigate(`/practitioners/${practitionerId}`)} />
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'start', minHeight: '100%' }}>
        <div style={{ marginBottom: 14, fontSize: 14 }}>Communication is strictly between you and the TruBalance Team. If you have questions about a particular scan, please leave comments on the actual scan to streamline communication. All other inquiries can be left here.</div>
        <Button variant="contained" color="secondary" onClick={() => setNewThreadOpen(true)} sx={{ mb: 2 }}>
          New Thread
        </Button>
        {allThreads.length === 0 && <Typography>No threads yet. Start a new one!</Typography>}
        {visibleThreads.map(thread => (
          <Accordion key={thread.id} expanded={expandedThread === thread.id} onChange={handleExpand(thread.id)}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              {hasUnread(thread) && <Chip label="Unread" color="secondary" size="small" style={{ marginRight: 8 }} />}
              <Typography>{thread.subject}</Typography>
              <Typography style={{ marginLeft: 'auto' }}>
                {thread.lastModified.toDate().toLocaleDateString()}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {thread.messages.map(msg => (
                  <ListItem key={msg.id} style={{ flexDirection: 'column', alignItems: isOwnMessage(msg) ? 'flex-end' : 'flex-start' }}>
                    <Box className="message-bubble" sx={{ background: isOwnMessage(msg) ? theme.Colors.GrayMediumLight : theme.Colors.GrayVeryLight, padding: 1, borderRadius: 1, maxWidth: '80%', position: 'relative' }}>
                      <Typography variant="caption"><strong>{msg.displayName}</strong> - {dayjs(msg.date.toDate()).format('MM/DD/YYYY h:mm A')}</Typography>
                      {editingMessages[msg.id] ? (
                        <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                        <TextField
                          value={editingMessages[msg.id]}
                          onChange={(e) => setEditingMessages(prev => ({ ...prev, [msg.id]: e.target.value }))}
                          multiline
                          fullWidth
                          slotProps={{ htmlInput: { maxLength: maxMessageLength }}}
                          helperText={`${editingMessages[msg.id].length} / ${maxMessageLength}`}
                          FormHelperTextProps={{ sx: { textAlign: 'right' }}}
                        />
                          <div style={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'flex-end' }}>
                            <IconButton onClick={() => handleSaveEdit(thread, msg)}><SaveIcon /></IconButton>
                            <IconButton onClick={() => handleCancelEdit(msg.id)}><CancelIcon /></IconButton>
                          </div>
                        </Box>
                      ) : (
                        <Typography sx={{ whiteSpace: 'pre-line' }}>{msg.text}</Typography>
                      )}
                      {isOwnMessage(msg) && !editingMessages[msg.id] && (
                        <div style={{ backgroundColor: theme.Colors.GrayDark, borderRadius: 8, position: 'absolute', bottom: '25%', right: 10}}>
                        <IconButton 
                          size="small"
                          variant="contained"
                          color="off"
                          onClick={() => handleStartEdit(msg.id, msg.text)}
                          sx={{
                            position: 'relative',
                            display: 'none',
                            '.message-bubble:hover &': { display: 'inline-flex' },
                          }}
                        ><EditIcon /></IconButton>
                        </div>
                      )}
                    </Box>
                  </ListItem>
                ))}
              </List>
              <Box display="flex" gap={1} mt={2}>
              <TextField
                label="Add Message"
                value={addMessageTexts[thread.id] || ''}
                onChange={(e) => setAddMessageTexts(prev => ({ ...prev, [thread.id]: e.target.value }))}
                multiline
                fullWidth
                slotProps={{ htmlInput: { maxLength: maxMessageLength }}}
                helperText={`${(addMessageTexts[thread.id] || '').length} / ${maxMessageLength}`}
                FormHelperTextProps={{ sx: { textAlign: 'right' }}}
              />
                <Button variant="contained" onClick={() => handleAddMessage(thread)} disabled={!!sendingMessages[thread.id]}>
                  {sendingMessages[thread.id] ? <CircularProgress size={24} /> : 'Send'}
                </Button>
              </Box>
              {hasOtherPartyMessage(thread) && (
                <Box display="flex" gap={1} mt={2}>
                {hasUnread(thread) && 
                  <Button color="secondary" variant="contained" fullWidth={true} onClick={() => markThreadAsRead(thread.id)} disabled={!!markingThreads[thread.id]}>
                    {markingThreads[thread.id] ? <CircularProgress size={24} /> : 'Mark as read'}
                  </Button>
                }
                {!hasUnread(thread) && 
                  <Button color="off" variant="contained" fullWidth={true} onClick={(e) => handleMarkThreadUnread(e, thread)} disabled={!!markingThreads[thread.id]}>
                    {markingThreads[thread.id] ? <CircularProgress size={24} /> : 'Mark unread'}
                  </Button>
                }
                </Box>
              )}
            </AccordionDetails>
          </Accordion>
        ))}
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
          <Pagination 
            count={totalPages} 
            page={currentPage} 
            onChange={(event, value) => setCurrentPage(value)} 
            color="secondary"
          />
          </div>
        )}
        <Dialog open={newThreadOpen} onClose={() => setNewThreadOpen(false)} sx={{ '& .MuiDialog-paper': { minWidth: '600px', padding: 1 } }} >
          <DialogTitle>New Thread</DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" gap={2} sx={{ padding: 1 }} >
              <TextField 
              label="Subject" 
              value={newSubject} 
              onChange={(e) => setNewSubject(e.target.value)} 
              fullWidth 
              slotProps={{ htmlInput: { maxLength: maxSubjectLength }}}
              helperText={`${newSubject.length} / ${maxSubjectLength}`}
              FormHelperTextProps={{ sx: { textAlign: 'right' }}}
            />
            <TextField 
              label="Message" 
              value={newMessageText} 
              onChange={(e) => setNewMessageText(e.target.value)} 
              multiline 
              rows={4} 
              fullWidth 
              slotProps={{ htmlInput: { maxLength: maxMessageLength }}}
              helperText={`${newMessageText.length} / ${maxMessageLength}`}
              FormHelperTextProps={{ sx: { textAlign: 'right' }}}
            />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button color="secondary" onClick={() => setNewThreadOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleNewThread} disabled={creatingThread}>
              {creatingThread ? <CircularProgress size={24} /> : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
}